import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Calendar, ChevronDown, Save, Loader2, ArrowLeft, Download, FileText, Plus, Trash2, Search } from 'lucide-react';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import toast from 'react-hot-toast';
import SearchableSelect from '../components/SearchableSelect';
import StaffPhoto from '../components/StaffPhoto';
import { useReferenceStore, ReferenceOption, ReferenceStore } from '../store/useReferenceStore';
import { ROW_HIGHLIGHT_COLORS, TEXT_HIGHLIGHT_COLORS } from '../constants/rowHighlightColors';

// Bank details, ordered the way someone making the payment reads them: who the
// account belongs to, then the number, then where it is held. Keys are the
// nurse/physio column names, which are identical on both records.
// Same list, and the same kind of control, the nurse registration form uses for
// each field — so a bank edited here behaves the way it does on the form.
// Bank and Branch read the shared reference lists (`bank_name`/`bank_branch`),
// which is what makes existing entries show up and lets a new one be added to
// the list from here.
const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat",
  "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh",
  "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan",
  "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  "Andaman and Nicobar Islands", "Chandigarh", "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi", "Jammu and Kashmir", "Ladakh", "Lakshadweep", "Puducherry",
];

type BankFieldKind = 'text' | 'select' | 'reference';
const BANK_FIELDS: { key: string; label: string; kind: BankFieldKind; category?: string; options?: string[] }[] = [
  { key: 'bank_account_name',   label: 'Account Name',   kind: 'text' },
  { key: 'bank_account_number', label: 'Account Number', kind: 'text' },
  { key: 'bank_account_type',   label: 'Account Type',   kind: 'select', options: ['Savings', 'Current', 'Salary'] },
  { key: 'bank_ifsc',           label: 'IFSC',           kind: 'text' },
  { key: 'bank_name',           label: 'Bank',           kind: 'reference', category: 'bank_name' },
  { key: 'bank_branch',         label: 'Branch',         kind: 'reference', category: 'bank_branch' },
  { key: 'bank_state',          label: 'State',          kind: 'select', options: INDIAN_STATES },
];

// The reference dropdowns are portaled out of the panel, so they need a class
// rather than an inline style. Sized to match `styles.bankInput` beside them.
const bankCellCss = `
  .bankcell { width: 100%; border: 1px solid #cbd5e1; border-radius: 4px; padding: 2px 5px;
              font-size: 0.76rem; font-weight: 600; color: #0f172a; outline: none;
              font-family: inherit; box-sizing: border-box; background: #fff; }
  .bankcell:focus { border-color: #2563eb; box-shadow: 0 0 0 2px rgba(37,99,235,0.12); }
`;

// --- Interfaces ---
interface Staff {
  id: number;
  full_name: string;
  profile: string;
  monthly_salary: number;
  // Bank details as captured on the registration form. Nurses and physios use
  // the same field names, so the panel below reads both without branching.
  bank_account_name?: string | null;
  bank_account_type?: string | null;
  bank_account_number?: string | null;
  bank_name?: string | null;
  bank_branch?: string | null;
  bank_state?: string | null;
  bank_ifsc?: string | null;
  photo_url?: string | null;
}

interface ClientData { client_name?: string; name?: string; }
interface PatientData { id: number; patient_name: string; client_name?: string; patient_clients?: ClientData[]; clients_info?: ClientData[]; }

interface Assignment {
  id: number; start_date: string; end_date: string; patient: PatientData; patient_id?: number;
  nurse_id?: number; physio_id?: number;
  shift_type: string; session?: string; rate: number; remarks?: string;
  // Freeform row highlight, hex color (e.g. '#fecaca') — null/undefined = none.
  row_color?: string | null;
  // Font color for the Remarks text — null/undefined = default text color.
  remarks_color?: string | null;
  actual_hours?: number; admin_approved_amount?: number; isTemp?: boolean;
}

interface DailyDeduction {
  record_id?: number; date: string; type: string; amount: number | ''; isUnsaved?: boolean; originalRecord?: any;
}

const formatDedDateForUI = (dbDate: string | null) => {
    if (!dbDate) return '';
    const parts = dbDate.split('T')[0].split('-');
    if (parts.length === 3 && parts[0].length === 4) return `${parts[2]}-${parts[1]}-${parts[0]}`;
    return dbDate;
};

const formatDedDateForDB = (uiDate: string | null) => {
    if (!uiDate) return null;
    const parts = uiDate.split(/[-/]/);
    if (parts.length === 3 && parts[2].length === 4) return `${parts[2]}-${parts[1]}-${parts[0]}`;
    if (parts.length === 3 && parts[0].length === 4) return uiDate;
    return null;
};

const getStandardHours = (shiftType: string): number => {
    if (!shiftType) return 0;
    const lower = shiftType.toLowerCase();
    if (lower.includes('24')) return 24;
    if (lower.includes('12')) return 12;
    if (lower.includes('8')) return 8;
    return 0;
};

const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getClientName = (patient: PatientData | undefined) => {
    if (!patient) return '';
    if (patient.client_name) return patient.client_name;
    if (patient.patient_clients && patient.patient_clients.length > 0) return patient.patient_clients[0].client_name || patient.patient_clients[0].name;
    if (patient.clients_info && patient.clients_info.length > 0) return patient.clients_info[0].client_name || patient.clients_info[0].name;
    return patient.patient_name; 
};

const getRowKey = (assign: Assignment | undefined, dateStr: string) => {
    if (!assign) return `${dateStr}_Manual`;
    if (assign.isTemp) return `temp_${assign.id}`;
    return `${dateStr}_assign_${assign.id}`; 
};

// --- CUSTOM SEARCHABLE DROPDOWN COMPONENT ---
const SearchableDropdown = ({ options, value, onChange, placeholder }: { options: {label: string, value: any}[], value: any, onChange: (val: any) => void, placeholder: string }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const wrapperRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        function handleClickOutside(event: MouseEvent) { if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) setIsOpen(false); }
        document.addEventListener("mousedown", handleClickOutside); return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filteredOptions = options.filter(o => o.label.toLowerCase().includes(searchTerm.toLowerCase()));
    const selectedOption = options.find(o => o.value === value);

    const isNoDuty = selectedOption?.label === 'No Duty' || selectedOption?.label === 'None / Specify...';
    const textColor = isNoDuty ? '#dc2626' : (selectedOption ? '#0f172a' : '#94a3b8');
    const textWeight = isNoDuty ? 700 : 600;

    return (
        <div ref={wrapperRef} style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center' }}>
            <div onClick={() => setIsOpen(!isOpen)} style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 8px', cursor: 'pointer', fontSize: '0.75rem', fontWeight: textWeight, color: textColor }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedOption ? selectedOption.label : placeholder}</span>
                <ChevronDown size={14} color="#64748b" />
            </div>
            {isOpen && (
                <div style={{ position: 'absolute', top: '100%', left: 0, width: '100%', minWidth: '200px', backgroundColor: 'white', border: '1px solid #cbd5e1', borderRadius: '6px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)', zIndex: 50, marginTop: '2px' }}>
                    <div style={{ padding: '8px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Search size={14} color="#94a3b8" />
                        <input autoFocus type="text" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ width: '100%', border: 'none', outline: 'none', fontSize: '0.8rem' }} />
                    </div>
                    <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                        {filteredOptions.length > 0 ? filteredOptions.map((opt, i) => (
                            <div key={i} onClick={() => { onChange(opt.value); setIsOpen(false); setSearchTerm(''); }}
                                style={{ padding: '8px 12px', fontSize: '0.8rem', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', backgroundColor: value === opt.value ? '#eff6ff' : 'white', color: value === opt.value ? '#2563eb' : '#334155' }}
                                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = value === opt.value ? '#eff6ff' : 'white'}
                            >
                                {opt.label}
                            </div>
                        )) : ( <div style={{ padding: '8px 12px', fontSize: '0.8rem', color: '#94a3b8', textAlign: 'center' }}>No results found</div> )}
                    </div>
                </div>
            )}
        </div>
    );
};

const SalarySheet: React.FC = () => {
  const navigate = useNavigate();
  
  // --- POLYMORPHIC PARAMS ---
  const { type = 'nurse', id } = useParams<{ type: string; id: string }>();
  const staffId = Number(id);
  const staffType = type; // 'nurse' or 'physio'
  
  const [searchParams] = useSearchParams();
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

  const [staff, setStaff] = useState<Staff | null>(null);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [patients, setPatients] = useState<PatientData[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [edits, setEdits] = useState<{[key: string]: {[field: string]: any}}>({});
  const [dailyDeductions, setDailyDeductions] = useState<Record<string, DailyDeduction>>({});

  // Bulk edit: drag across rows to select, then set Scale / Salary across all of them.
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  // Freeform row highlight — applied to every currently-selected row at once.
  // Deliberately NOT flagged isUnsaved: that flag drives the "unsaved" yellow
  // warning, which should never mask a highlight the user just picked.
  // needsSave is what actually gets it included on the next Save Changes —
  // see the save loop's gate above.
  const applyRowColor = (color: string | null) => {
    setEdits(prev => {
      const next = { ...prev };
      selectedRows.forEach(k => { next[k] = { ...(next[k] || {}), row_color: color, needsSave: true }; });
      return next;
    });
  };
  // Font color for the Remarks text — same targeting, independent field.
  const applyRemarksColor = (color: string | null) => {
    setEdits(prev => {
      const next = { ...prev };
      selectedRows.forEach(k => { next[k] = { ...(next[k] || {}), remarks_color: color, needsSave: true }; });
      return next;
    });
  };
  // Column fill: click a fillable column header → the whole column is selected;
  // typing a value in any of its cells fills every row. Esc / re-click clears it.
  const [selectedColumn, setSelectedColumn] = useState<string | null>(null);
  const [selectedCells, setSelectedCells] = useState<Set<string>>(new Set());  // cell keys selected within selectedColumn
  const cellDrag = useRef<{ field: string; order: string[]; anchorIdx: number } | null>(null);
  const FILL_COLUMN_LABELS: Record<string, string> = { monthlyScale: 'Scale', admin_approved_amount: 'Salary', shift_type: 'Shift Type', remarks: 'Remarks', ded_type: 'TID/Type', ded_amount: 'Amount', perHr: 'Per Hr', actual_hours: 'Hrs', shifts_count: 'Shifts' };
  // Row order for a column's range selection (deduction columns run over dates).
  const columnOrder = (field: string): string[] =>
    (field === 'ded_type' || field === 'ded_amount') ? getDaysInMonth().map(d => formatDate(d)) : getSelectableRowKeys();
  const clearColumnSel = () => { setSelectedColumn(null); setSelectedCells(new Set()); };
  // Click a column header → select the whole column (toggle off if already selected).
  const pickColumn = (field: string) => {
    if (selectedColumn === field) { clearColumnSel(); return; }
    setSelectedRows(new Set());
    setSelectedColumn(field);
    setSelectedCells(new Set(columnOrder(field)));
  };
  // Press on a fillable cell → begin a range in that column (keeps an existing
  // selection if you press inside it, so you can click in and type).
  const startCellSel = (field: string, key: string, e: React.MouseEvent) => {
    e.stopPropagation();  // don't also trigger the whole-row drag
    const order = columnOrder(field);
    const idx = order.indexOf(key);
    if (idx === -1) return;
    cellDrag.current = { field, order, anchorIdx: idx };
    setSelectedRows(new Set());
    if (!(selectedColumn === field && selectedCells.has(key))) {
      setSelectedColumn(field);
      setSelectedCells(new Set([key]));
    }
  };
  // Drag over more cells in the same column → grow the range.
  const extendCellSel = (field: string, key: string) => {
    const cd = cellDrag.current;
    if (!cd || cd.field !== field) return;
    const idx = cd.order.indexOf(key);
    if (idx === -1 || idx === cd.anchorIdx) return;
    (document.activeElement as HTMLElement | null)?.blur();
    window.getSelection()?.removeAllRanges();
    const [lo, hi] = idx < cd.anchorIdx ? [idx, cd.anchorIdx] : [cd.anchorIdx, idx];
    setSelectedColumn(field);
    setSelectedCells(new Set(cd.order.slice(lo, hi + 1)));
  };
  const dragging = useRef(false);        // a drag-select is in progress
  const dragAnchor = useRef<number | null>(null);   // index of the row where the drag started
  const dragAdditive = useRef<Set<string>>(new Set()); // selection that existed before this drag (for Ctrl-drag)
  const pendingFromControl = useRef(false); // pressed inside an input — armed, but not yet a drag
  const tableRef = useRef<HTMLTableElement>(null);   // for click-outside deselect
  // The floating selection toolbar (row-count toast + color swatches) renders
  // outside the table, so a click on it would otherwise register as "outside
  // the table" and clear the selection it's meant to act on.
  const toolbarRef = useRef<HTMLDivElement>(null);

  // End any drag when the mouse is released anywhere on the page.
  useEffect(() => {
    const up = () => { dragging.current = false; dragAnchor.current = null; pendingFromControl.current = false; };
    window.addEventListener('mouseup', up);
    return () => window.removeEventListener('mouseup', up);
  }, []);
  
  const [hraEnabled, setHraEnabled] = useState<boolean>(false); const [hraValue, setHraValue] = useState<number>(0);
  const [allowanceEnabled, setAllowanceEnabled] = useState<boolean>(false); const [allowanceValue, setAllowanceValue] = useState<number>(0);
  const [hostelEnabled, setHostelEnabled] = useState<boolean>(true); const [hostelValue, setHostelValue] = useState<number>(2000);
  const [ptEnabled, setPtEnabled] = useState<boolean>(false); const [ptValue, setPtValue] = useState<number>(0);
  const [tdsEnabled, setTdsEnabled] = useState<boolean>(true); const [tdsPercent, setTdsPercent] = useState<number>(10);
  // Sub Total typed over on the footer. null = not overridden, so the sum of
  // the Salary column is used and keeps tracking edits made in the table.
  const [subtotalOverride, setSubtotalOverride] = useState<number | null>(null);
  // What's in the Sub Total box while it has focus. The box holds the real
  // figure (not a placeholder) so it can be backspaced and edited like any
  // other number; null when not focused.
  const [subtotalDraft, setSubtotalDraft] = useState<string | null>(null);
  // Bank details block: read-only until Edit is pressed, then an explicit
  // Save/Cancel. Deliberately not save-on-blur — a half-typed account number
  // must never reach the record.
  const [bankEditing, setBankEditing] = useState(false);
  const [bankDraft, setBankDraft] = useState<Record<string, string>>({});
  const [bankSaving, setBankSaving] = useState(false);
  // Same reference lists the registration form's Bank / Branch dropdowns read.
  const refOptions = useReferenceStore((s: ReferenceStore) => s.options);
  const addReferenceOption = useReferenceStore((s: ReferenceStore) => s.addOption);

  const [selectedMonth, setSelectedMonth] = useState(searchParams.get('month') ? Number(searchParams.get('month')) : new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(searchParams.get('year') ? Number(searchParams.get('year')) : new Date().getFullYear());

  const [isMonthOpen, setIsMonthOpen] = useState(false); const [isYearOpen, setIsYearOpen] = useState(false);
  const monthRef = useRef<HTMLDivElement>(null); const yearRef = useRef<HTMLDivElement>(null);

  const months = [ { val: 1, label: 'January' }, { val: 2, label: 'February' }, { val: 3, label: 'March' }, { val: 4, label: 'April' }, { val: 5, label: 'May' }, { val: 6, label: 'June' }, { val: 7, label: 'July' }, { val: 8, label: 'August' }, { val: 9, label: 'September' }, { val: 10, label: 'October' }, { val: 11, label: 'November' }, { val: 12, label: 'December' } ];
  const years = [2024, 2025, 2026, 2027];

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (monthRef.current && !monthRef.current.contains(event.target as Node)) setIsMonthOpen(false);
      if (yearRef.current && !yearRef.current.contains(event.target as Node)) setIsYearOpen(false);
    }
    document.addEventListener("mousedown", handleClickOutside); return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchPatients = async () => {
    try {
        const res = await axios.get(`${API_BASE_URL}/api/patients/`, { headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` } });
        setPatients(Array.isArray(res.data) ? res.data : (res.data.results || []));
    } catch (err) { console.error("Failed to load patients", err); }
  };

  const getProfileEndpoint = () => staffType === 'physio' ? 'physiotherapists' : 'nurses';

  const fetchData = async () => {
    // setBankEditing(false): switching staff or month reloads `staff`, so an
    // open bank form would otherwise be left showing one nurse's account
    // details against another's sheet.
    if (!staffId) return; setLoading(true); setEdits({}); setAssignments([]); setDailyDeductions({}); setSelectedRows(new Set()); setBankEditing(false);
    const authHeader = { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` };

    try {
      const staffRes = await axios.get(`${API_BASE_URL}/api/${getProfileEndpoint()}/${staffId}`, { headers: authHeader });
      setStaff(staffRes.data);

      const startStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-01`;
      const lastDay = new Date(selectedYear, selectedMonth, 0).getDate();
      const endStr = `${selectedYear}-${String(selectedMonth).padStart(2, '0')}-${lastDay}`;

      // NEW two-layer flow: salary rows come from shift_actuals (downstream of the grid).
      // Deductions come from the dedicated salary_deductions table.
      const actualParams: any = { start: startStr, end: endStr };
      if (staffType === 'physio') actualParams.physio_id = staffId; else actualParams.nurse_id = staffId;

      const [actRes, dedRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/schedule/salary/actuals`, { headers: authHeader, params: actualParams }),
        axios.get(`${API_BASE_URL}/api/schedule/deductions`, { headers: authHeader, params: { staff_id: staffId, staff_type: staffType, start: startStr, end: endStr } }),
      ]);

      const actuals: any[] = Array.isArray(actRes.data) ? actRes.data : [];
      const deductions: any[] = Array.isArray(dedRes.data) ? dedRes.data : [];

      // Each actual → one pseudo "assignment" + a pre-filled edit row, so the existing
      // render + payroll math run unchanged. From this staff's view their own row is
      // always the staff (role is irrelevant here), so isAssistant never triggers.
      const pseudoAssignments: Assignment[] = [];
      const loadedEdits: any = {};
      const loadedDeductions: Record<string, DailyDeduction> = {};

      actuals.forEach((a: any) => {
        const date = String(a.date).split('T')[0];
        pseudoAssignments.push({
          id: a.id, isTemp: false, start_date: date, end_date: date,
          patient: { id: a.patient_id || 0, patient_name: '' },
          patient_id: a.patient_id,
          nurse_id:  staffType !== 'physio' ? Number(staffId) : undefined,
          physio_id: staffType === 'physio'  ? Number(staffId) : undefined,
          shift_type: a.shift_type, rate: a.rate || 0,
          actual_hours: a.salary_hours, admin_approved_amount: a.admin_approved_amount,
          remarks: a.remarks, row_color: a.row_color, remarks_color: a.remarks_color,
        } as Assignment);

        const rowKey = `${date}_assign_${a.id}`;
        loadedEdits[rowKey] = {
          record_id: a.id, assignment_id: a.id, actual_id: a.id,
          isDeleted: !!a.is_omitted_salary,
          patient_id: a.patient_id,
          shift_type: a.shift_type,
          monthlyScale: a.monthly_scale,
          actual_hours: a.salary_hours,           // SALARY-block hours (independent of casesheet)
          admin_approved_amount: a.admin_approved_amount,
          remarks: a.remarks,
          row_color: a.row_color,
          remarks_color: a.remarks_color,
          shifts_count: a.salary_shifts !== null && a.salary_shifts !== undefined ? Number(a.salary_shifts) : 1,
        };
      });

      deductions.forEach((d: any) => {
        const date = String(d.date).split('T')[0];
        loadedDeductions[date] = {
          record_id: d.id,
          date: d.deduction_date ? formatDedDateForUI(d.deduction_date) : '',
          type: d.deduction_type || '',
          amount: Number(d.amount) || '',
          isUnsaved: false,
          originalRecord: d,
        };
      });

      setAssignments(pseudoAssignments); setEdits(loadedEdits); setDailyDeductions(loadedDeductions);
    } catch (error) { console.error("Critical Error in fetchData:", error); } finally { setLoading(false); }
  };

  const fetchSettings = async () => {
      try {
          const res = await axios.get(`${API_BASE_URL}/api/salary-settings`, { params: { staff_id: staffId, staff_type: staffType, month: selectedMonth, year: selectedYear }, headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` } });
          if(res.data) {
            setHraEnabled(res.data.hra_enabled ?? false); setHraValue(res.data.hra_amount ?? 0); 
            setAllowanceEnabled(res.data.allowance_enabled ?? false); setAllowanceValue(res.data.allowance_amount ?? 0);
            setHostelEnabled(res.data.hostel_enabled ?? true); setHostelValue(res.data.hostel_amount ?? 2000); 
            setPtEnabled(res.data.pt_enabled ?? false); setPtValue(res.data.pt_amount ?? 0); 
            setTdsEnabled(res.data.tds_enabled ?? true); setTdsPercent(res.data.tds_percent ?? 10);
            setSubtotalOverride(res.data.subtotal_override ?? null);
          } else {
            // No saved settings for this staff/month — clear any override left
            // over from the sheet that was open before, so one nurse's typed
            // Sub Total can't be shown against another's month.
            setSubtotalOverride(null);
          }
      } catch (err) { console.error("Failed to load settings", err); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); fetchSettings(); fetchPatients(); }, [staffId, staffType, selectedMonth, selectedYear]);

  // Fields that fill across a multi-row selection when edited in-column (Scale, Salary).
  // admin_approved_amount is the Salary column's field — it kept its name when the
  // separate Approved column was removed and Salary became the editable payable.
  const BULK_FILL_FIELDS = ['monthlyScale', 'admin_approved_amount'];

  const handleInputChange = (key: string, field: string, value: any) => {
    // Any edit that can move the Salary column (everything but Remarks) drops a
    // typed-over Sub Total, so the footer picks up the new column total.
    if (field !== 'remarks') setSubtotalOverride(null);
    // Apply the same field-edit to one row (with the usual recompute side-effects).
    const applyField = (base: any) => {
      const newRow = { ...(base || {}), [field]: value, isUnsaved: true };
      if (field === 'patient_id' && value !== 0 && newRow.monthlyScale === undefined) {
          newRow.monthlyScale = staff?.monthly_salary || 0;
      }
      if (field === 'shift_type' && value) {
          newRow.actual_hours = getStandardHours(value);
      }
      if (['actual_hours', 'monthlyScale', 'shift_type', 'patient_id'].includes(field)) {
          newRow.admin_approved_amount = undefined;
      }
      // Emptying the Remark clears any highlight/text color too — an empty
      // remark shouldn't be left looking flagged. Shown as plain/white right
      // away rather than the yellow "unsaved" warning (isUnsaved: false
      // overrides the default set above) — still correctly saved via
      // needsSave, same mechanism the color toolbar uses.
      if (field === 'remarks' && value === '') {
          newRow.row_color = null;
          newRow.remarks_color = null;
          newRow.isUnsaved = false;
          newRow.needsSave = true;
      }
      return newRow;
    };

    // Spreadsheet-style fill: editing Scale/Salary on a selected row applies the
    // value to every selected row at once. Other fields (or a single selection)
    // only affect the edited row.
    const spread = BULK_FILL_FIELDS.includes(field) && selectedRows.has(key) && selectedRows.size > 1;
    // Column range fill: this cell is in the selected column range → set every selected cell.
    const fillCol = selectedColumn === field && selectedCells.has(key) && ['monthlyScale', 'admin_approved_amount', 'shift_type', 'remarks'].includes(field);

    setEdits(prev => {
      if (fillCol) {
        const next = { ...prev };
        selectedCells.forEach(k => { next[k] = applyField(next[k]); });
        return next;
      }
      if (!spread) return { ...prev, [key]: applyField(prev[key]) };
      const next = { ...prev };
      selectedRows.forEach(k => { next[k] = applyField(next[k]); });
      return next;
    });
  };

  const handleAddShift = (dateStr: string) => { setAssignments(prev => [...prev, { id: Date.now(), isTemp: true, start_date: dateStr, end_date: dateStr, nurse_id: staffType !== 'physio' ? staffId : undefined, physio_id: staffType === 'physio' ? staffId : undefined, patient: { id: 0, patient_name: '' }, shift_type: '', rate: 0 }]); };

  const handleRemoveShift = (rowKey: string, assign: Assignment) => {
      setSubtotalOverride(null); // the column total changes, so a typed Sub Total no longer applies
      if (assign.isTemp && !edits[rowKey]?.record_id) {
          setAssignments(prev => prev.filter(a => a.id !== assign.id)); setEdits(prev => { const n = {...prev}; delete n[rowKey]; return n; });
      } else { setEdits(prev => ({ ...prev, [rowKey]: { ...prev[rowKey], isDeleted: true, isUnsaved: true } })); }
  };

  const handleDeductionChange = (dateStr: string, field: string, value: any) => {
      // Column range fill: this deduction cell is in the selected range → set every selected day.
      const dedCol = field === 'type' ? 'ded_type' : 'ded_amount';
      const fillCol = selectedColumn === dedCol && selectedCells.has(dateStr);
      setDailyDeductions(prev => {
        if (!fillCol) return { ...prev, [dateStr]: { ...(prev[dateStr] || { date: '', type: '', amount: '' }), [field]: value, isUnsaved: true } };
        const next = { ...prev };
        selectedCells.forEach(ds => {
          next[ds] = { ...(next[ds] || { date: '', type: '', amount: '' }), [field]: value, isUnsaved: true };
        });
        return next;
      });
  };

  // End a cell-range drag on mouse-up; Esc or a click outside the grid clears it.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') clearColumnSel(); };
    const onUp = () => { cellDrag.current = null; };
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (tableRef.current && !tableRef.current.contains(t) && !toolbarRef.current?.contains(t)) clearColumnSel();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('mouseup', onUp);
    document.addEventListener('mousedown', onDown);
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('mouseup', onUp); document.removeEventListener('mousedown', onDown); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Bulk row selection + apply (Scale / Salary) ─────────────────────────────
  // Row keys that carry the editable Scale/Salary inputs (i.e. not "No Duty"),
  // mirroring the render's row iteration so Select-All matches what's on screen.
  const getSelectableRowKeys = (): string[] => {
    const keys: string[] = [];
    getDaysInMonth().forEach(dateObj => {
      const dateStr = formatDate(dateObj);
      const matches = assignments.filter(a => dateStr >= a.start_date && dateStr <= a.end_date);
      const visibleMatches = matches.filter(assign => {
        const rk = getRowKey(assign, dateStr); const es = edits[rk];
        return !(es?.isDeleted && !es?.isUnsaved);
      });
      const rowsToRender = visibleMatches.length > 0 ? visibleMatches : (matches.length > 0 ? [matches[0]] : [undefined]);
      rowsToRender.forEach(assign => {
        const rk = getRowKey(assign, dateStr);
        if (!edits[rk]?.isDeleted) keys.push(rk); // "No Duty" rows have no Scale/Salary to set
      });
    });
    return keys;
  };

  // Begin a drag-select on a row — from anywhere in it, inputs included.
  //
  // Pressing on an input only *arms* the drag: the selection is left untouched
  // and the click is allowed through, so the field focuses normally and a range
  // you already selected survives while you go type into one of its cells. The
  // drag only takes over once the pointer actually reaches a different row.
  const startDrag = (rowKey: string, e: React.MouseEvent) => {
    const keys = getSelectableRowKeys();
    const idx = keys.indexOf(rowKey);
    if (idx === -1) return; // "No Duty" rows aren't selectable
    clearColumnSel(); // a whole-row drag isn't a column-range selection

    dragging.current = true;
    dragAnchor.current = idx;
    dragAdditive.current = (e.ctrlKey || e.metaKey) ? new Set(selectedRows) : new Set();

    const tag = (e.target as HTMLElement).tagName.toUpperCase();
    if (['INPUT', 'SELECT', 'OPTION', 'BUTTON', 'TEXTAREA', 'SVG', 'PATH'].includes(tag)) {
      pendingFromControl.current = true;
      return;
    }

    pendingFromControl.current = false;
    e.preventDefault(); // suppress text selection during the drag
    const base = new Set(dragAdditive.current);
    base.add(rowKey);
    setSelectedRows(base);
  };


  // Deselect when the click lands outside the grid. Cheap to attach because it
  // only exists while something is actually selected.
  useEffect(() => {
    if (selectedRows.size === 0) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (tableRef.current && !tableRef.current.contains(t) && !toolbarRef.current?.contains(t)) {
        setSelectedRows(new Set());
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [selectedRows.size]);

  // Extend the selection to the row currently under the cursor while dragging.
  const extendDrag = (rowKey: string) => {
    if (!dragging.current || dragAnchor.current == null) return;
    const keys = getSelectableRowKeys();
    const idx = keys.indexOf(rowKey);
    if (idx === -1) return;

    if (pendingFromControl.current) {
      if (idx === dragAnchor.current) return; // still on the row we pressed in
      // A real drag has begun. Drop focus so the input stops extending its own
      // text selection, and clear any stray range the press already started.
      pendingFromControl.current = false;
      (document.activeElement as HTMLElement | null)?.blur();
    }
    window.getSelection()?.removeAllRanges();

    const lo = Math.min(dragAnchor.current, idx);
    const hi = Math.max(dragAnchor.current, idx);
    const base = new Set(dragAdditive.current);
    keys.slice(lo, hi + 1).forEach(k => base.add(k));
    setSelectedRows(base);
  };

  const handleSave = async () => {
    setSaving(true);
    const token = localStorage.getItem('accessToken');
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
    const promises: Promise<any>[] = [];

    // 1. Salary edits → PATCH the salary side of each actual (one-way: never touches schedule).
    //    Rows are backed by an actual_id (from the grid). Manual rows without one can't
    //    persist here — shifts are added on the grid in the new flow.
    // Rows added on this sheet (the "+" button, or typing into an empty date)
    // have no actual behind them and are listed here when they can't be created
    // — patient_id, date and shift_type are all NOT NULL on shift_actuals. They
    // used to hit a bare `return` and be discarded silently on save, taking the
    // whole row with them.
    const blockedRows: string[] = [];

    Object.keys(edits).forEach(key => {
      const changes = edits[key];
      // needsSave covers edits that must still be persisted but are
      // deliberately NOT flagged isUnsaved (the row-color/text-color toolbar,
      // and clearing a remark) — see applyRowColor/applyRemarksColor and
      // applyField's remarks-clear branch below.
      if (!changes.isUnsaved && !changes.isDeleted && !changes.isReset && !changes.needsSave) return;

      const actualId = changes.actual_id || changes.record_id;
      if (!actualId) {
        // Never persisted, so there is nothing to omit or reset — just drop it.
        if (changes.isDeleted || changes.isReset) return;

        const tempAssign = assignments.find(a => a.isTemp && getRowKey(a, '') === key);
        const rowDate = tempAssign ? tempAssign.start_date : (key.indexOf('_') > -1 ? key.substring(0, key.indexOf('_')) : '');
        const patientId = Number(changes.patient_id ?? tempAssign?.patient?.id ?? 0);
        const shiftType = changes.shift_type ?? tempAssign?.shift_type ?? '';

        if (!rowDate || !patientId || !shiftType) {
          blockedRows.push(rowDate || 'a new row');
          return;
        }

        const calc = calculateDailyValues(tempAssign, changes);
        promises.push(axios.post(`${API_BASE_URL}/api/schedule/actuals/salary`, {
          patient_id: patientId,
          date: rowDate,
          shift_type: shiftType,
          nurse_id:  staffType !== 'physio' ? Number(staffId) : null,
          physio_id: staffType === 'physio'  ? Number(staffId) : null,
          monthly_scale: Number(calc.monthlyScale) || 0,
          salary_hours: changes.actual_hours !== undefined && changes.actual_hours !== '' ? Number(changes.actual_hours) : Number(calc.hrs || 0),
          admin_approved_amount: changes.admin_approved_amount !== undefined && changes.admin_approved_amount !== '' ? Number(changes.admin_approved_amount) : Number(calc.approved || 0),
          salary_shifts: changes.shifts_count !== undefined && changes.shifts_count !== '' ? Number(changes.shifts_count) : 1,
          remarks: changes.remarks ?? '',
          row_color: changes.row_color ?? null,
          remarks_color: changes.remarks_color ?? null,
        }, { headers }));
        return;
      }

      const dateStr = key.indexOf('_') > -1 ? key.substring(0, key.indexOf('_')) : '';
      const original = assignments.find(a => !a.isTemp && getRowKey(a, dateStr) === key);
      const calculated = calculateDailyValues(original, changes);

      const salaryPayload: any = {
        monthly_scale: Number(calculated.monthlyScale) || 0,
        salary_hours: changes.actual_hours !== undefined && changes.actual_hours !== '' ? Number(changes.actual_hours) : Number(calculated.hrs || 0),
        admin_approved_amount: changes.admin_approved_amount !== undefined && changes.admin_approved_amount !== '' ? Number(changes.admin_approved_amount) : Number(calculated.approved || 0),
        remarks: changes.remarks !== undefined ? changes.remarks : (original?.remarks || ''),
        row_color: changes.row_color !== undefined ? changes.row_color : (original?.row_color ?? null),
        remarks_color: changes.remarks_color !== undefined ? changes.remarks_color : (original?.remarks_color ?? null),
        is_omitted_salary: !!changes.isDeleted,
        salary_shifts: changes.shifts_count !== undefined && changes.shifts_count !== '' ? Number(changes.shifts_count) : (changes.isDeleted ? 0 : 1),
      };

      if (changes.isReset) {
        // Reset = drop the manual override and re-pull from the source schedule shift.
        promises.push(axios.post(`${API_BASE_URL}/api/schedule/actuals/${actualId}/salary/reset`, {}, { headers }));
        return;
      }
      if (changes.isDeleted) {
        salaryPayload.salary_hours = 0;
        salaryPayload.admin_approved_amount = 0;
        salaryPayload.salary_shifts = 0;
        salaryPayload.remarks = 'Omitted from Salary Sheet';
      }
      promises.push(axios.patch(`${API_BASE_URL}/api/schedule/actuals/${actualId}/salary`, salaryPayload, { headers }));
    });

    // 2. Deductions → dedicated salary_deductions table (standalone, independent of shifts).
    const dedPayload: any[] = [];
    Object.keys(dailyDeductions).forEach(dateStr => {
      const ded = dailyDeductions[dateStr];
      if (!ded.isUnsaved) return;
      const isBlank = (ded.amount === '' || ded.amount === 0) && ded.type === '';
      dedPayload.push({
        staff_id: Number(staffId), staff_type: staffType, date: dateStr,
        deduction_date: isBlank ? null : (ded.date ? formatDedDateForDB(ded.date) : dateStr),
        deduction_type: isBlank ? null : ded.type,
        amount: isBlank ? null : Number(ded.amount),  // blank → backend deletes the row
      });
    });
    if (dedPayload.length > 0) {
      promises.push(axios.post(`${API_BASE_URL}/api/schedule/deductions/bulk`, dedPayload, { headers }));
    }
    // 3. Monthly settings
    promises.push(axios.post(`${API_BASE_URL}/api/salary-settings`, { staff_id: staffId, staff_type: staffType, month: selectedMonth, year: selectedYear, hra_enabled: hraEnabled, hra_amount: hraValue, allowance_enabled: allowanceEnabled, allowance_amount: allowanceValue, hostel_enabled: hostelEnabled, hostel_amount: hostelValue, pt_enabled: ptEnabled, pt_amount: ptValue, tds_enabled: tdsEnabled, tds_percent: tdsPercent, subtotal_override: subtotalOverride, net_salary: Math.round(netTakeHome) }, { headers }));

    try {
      await Promise.all(promises);
      setSelectedRows(new Set());
      toast.success('Changes saved successfully');
      // Said before the reload wipes them: a new row needs a customer and a
      // shift type before it can be stored, and staying silent here is what
      // made the row look like it had saved when it hadn't.
      if (blockedRows.length > 0) {
        toast.error(
          `Not saved — pick a Customer Name and Shift Type first: ${blockedRows.join(', ')}`,
          { duration: 8000 }
        );
      }
      fetchData();
      fetchSettings();
    } catch (error: any) {
      console.error(error);
      // 409 = the slot already belongs to someone else; the backend explains why.
      alert(error?.response?.data?.detail || "Failed to save.");
    } finally { setSaving(false); }
  };

  const getDaysInMonth = () => { const date = new Date(selectedYear, selectedMonth - 1, 1); const days = []; while (date.getMonth() === selectedMonth - 1) { days.push(new Date(date)); date.setDate(date.getDate() + 1); } return days; };

  const checkIfModified = (assign: Assignment | undefined, rowKey: string) => {
      if (edits[rowKey]?.isUnsaved) return 'unsaved'; if (edits[rowKey]?.record_id) return 'modified'; 
      if (assign && !assign.isTemp) {
          const stdHrs = getStandardHours(assign.shift_type);
          if (assign.actual_hours !== null && assign.actual_hours !== undefined && assign.actual_hours !== stdHrs) return 'modified';
          if (assign.remarks && assign.remarks.trim() !== '') return 'modified';
          if (assign.admin_approved_amount !== null && assign.admin_approved_amount !== undefined && Math.abs(assign.admin_approved_amount - Math.round((assign.rate / stdHrs) * (assign.actual_hours || stdHrs))) > 1) return 'modified';
      }
      return 'clean';
  };

  const calculateDailyValues = (assign: Assignment | undefined, editRow: any) => {
    const isAssistant = assign && (staffType === 'physio'
      ? Number((assign as any).second_physio_id) === Number(staffId)
      : Number((assign as any).second_nurse_id)  === Number(staffId));

    const currentShiftType = editRow.shift_type !== undefined
        ? editRow.shift_type
        : (isAssistant
            ? ((assign as any).second_shift_type || assign?.shift_type || '')
            : (assign?.shift_type || ''));
    const stdHrs = currentShiftType ? getStandardHours(currentShiftType) : 0;

    let monthlyScale: number | string = 0;
    if (editRow.monthlyScale !== undefined && editRow.monthlyScale !== null && editRow.monthlyScale !== 0) {
        monthlyScale = editRow.monthlyScale;
    } else if (assign) {
        if (isAssistant) {
            if ((assign as any).second_monthly_scale) monthlyScale = (assign as any).second_monthly_scale;
            else if ((assign as any).second_rate) monthlyScale = (assign as any).second_rate * 30;
            else if (staff?.monthly_salary) monthlyScale = staff.monthly_salary;
        } else {
            if ((assign as any).monthly_scale) monthlyScale = (assign as any).monthly_scale;
            else if (assign.rate && assign.rate !== 0) monthlyScale = assign.rate * 30;
            else if (staff?.monthly_salary) monthlyScale = staff.monthly_salary;
        }
    } else if (staff?.monthly_salary) {
        monthlyScale = staff.monthly_salary;
    }
    
    let scaleNum = Number(monthlyScale) || 0;

    let hrs: number | string = '';
    if (editRow.actual_hours !== undefined && editRow.actual_hours !== null && editRow.actual_hours !== 0) {
        hrs = editRow.actual_hours; 
    } else if (assign && assign.actual_hours && assign.actual_hours !== 0) {
        hrs = assign.actual_hours;  
    } else if (currentShiftType) {
        hrs = stdHrs;               
    }

    const hrsNum = Number(hrs) || 0;
    const perHr = (scaleNum / 30) > 0 && stdHrs > 0 ? ((scaleNum / 30) / stdHrs) : 0;
    const salary = perHr * hrsNum;

    let approved = 0;
    const storedApproved = editRow.admin_approved_amount;
    if (storedApproved !== undefined && storedApproved !== null && storedApproved !== '' && Number(storedApproved) !== 0) {
        approved = Number(storedApproved);
    } else if (assign && editRow.actual_hours === undefined && editRow.shift_type === undefined && editRow.monthlyScale === undefined) {
        if (isAssistant) {
            approved = (assign as any).second_rate || Math.round(salary);
        } else {
            approved = assign.rate || Math.round(salary);
        }
    } else {
        approved = Math.round(salary);
    }

    return { monthlyScale, hrs, perHr, salary, approved, currentShiftType };
};

  const totals = useMemo(() => {
      let salarySum = 0; let approvedSum = 0; let shiftsSum = 0;
      getDaysInMonth().forEach(dateObj => {
          const dateStr = formatDate(dateObj);
          const matches = assignments.filter(a => dateStr >= a.start_date && dateStr <= a.end_date);
          
          const allKeys = [...matches.map(a => getRowKey(a, dateStr)), ...Object.keys(edits).filter(key => key.startsWith('temp_') ? assignments.find(x => x.id === Number(key.split('_')[1]))?.start_date === dateStr : key.startsWith(dateStr) && !assignments.some(a => !a.isTemp && getRowKey(a, dateStr) === key))];
          
          Array.from(new Set(allKeys)).forEach(rowKey => {
              if (edits[rowKey]?.isDeleted) return; 
              
              const assign = rowKey.startsWith('temp_') ? assignments.find(a => a.id === Number(rowKey.split('_')[1])) : assignments.find(a => !a.isTemp && getRowKey(a, dateStr) === rowKey);
              const vals = calculateDailyValues(assign, edits[rowKey] || {});
              
              const patientId = edits[rowKey]?.patient_id !== undefined ? edits[rowKey].patient_id : assign?.patient?.id;
              
              if (edits[rowKey]?.shifts_count !== undefined && edits[rowKey]?.shifts_count !== '') {
                  shiftsSum += Number(edits[rowKey].shifts_count);
              } else if (patientId && patientId !== 0) {
                  shiftsSum += 1; 
              }

              salarySum += vals.salary; 
              approvedSum += vals.approved;
          });
      });
      return { salarySum, approvedSum, shiftsSum, deductionsSum: Object.values(dailyDeductions).reduce((sum, ded) => sum + (Number(ded.amount) || 0), 0) };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignments, edits, dailyDeductions, selectedMonth, selectedYear]);

  const hraAmount = hraEnabled ? hraValue : 0;
  const allowanceAmount = allowanceEnabled ? allowanceValue : 0;
  const hostelAmount = hostelEnabled ? hostelValue : 0;
  // The figure the whole footer is built on: the Salary column's sum, unless an
  // admin has typed over it on the Sub Total row.
  const salarySubtotal = subtotalOverride ?? totals.approvedSum;
  const grossSalary = salarySubtotal + hraAmount + allowanceAmount - hostelAmount;
  const ptAmount = ptEnabled ? ptValue : 0;
  const tdsAmount = tdsEnabled && tdsPercent > 0 ? (grossSalary * tdsPercent) / 100 : 0;
  const advances = totals.deductionsSum;
  const finalTotalDeductions = advances + ptAmount + tdsAmount;
  // Gross - TDS - Professional Tax - Advances.
  const netTakeHome = grossSalary - finalTotalDeductions;

  // --- EXPORT: CSV ---
  const handleExportCSV = () => {
    const monthLabel = months.find(m => m.val === selectedMonth)?.label || '';
    const staffName = staff?.full_name || 'Staff';
    const headers = ['Date', 'Week', 'Customer Name', 'Remarks', 'Shift Type', 'Scale', 'Per Hr', 'Hrs', 'Shifts', 'Salary', 'Approved', 'Ded Date', 'TID/Type', 'Amount'];
    const rows: string[][] = [headers];

    getDaysInMonth().forEach(dateObj => {
      const dateStr = formatDate(dateObj);
      const displayDate = `${String(dateObj.getDate()).padStart(2, '0')}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${dateObj.getFullYear()}`;
      const dayOfWeek = dateObj.toLocaleDateString('en-US', { weekday: 'long' });
      const matches = assignments.filter(a => dateStr >= a.start_date && dateStr <= a.end_date);
      const visibleMatches = matches.filter(a => !edits[getRowKey(a, dateStr)]?.isDeleted || edits[getRowKey(a, dateStr)]?.isUnsaved);
      const rowsToRender = visibleMatches.length > 0 ? visibleMatches : (matches.length > 0 ? [matches[0]] : [undefined]);
      const ded = dailyDeductions[dateStr] || { date: '', type: '', amount: '' };

      rowsToRender.forEach((assign, idx) => {
        const rowKey = getRowKey(assign, dateStr);
        const editRow = edits[rowKey] || {};
        const isDeleted = !!editRow.isDeleted;
        const vals = calculateDailyValues(isDeleted ? undefined : assign, editRow);
        const patientId = editRow.patient_id ?? assign?.patient?.id;
        const patient = patients.find(p => p.id === patientId);
        const customerName = isDeleted ? 'No Duty' : (patient ? (getClientName(patient) || patient.patient_name) : 'No Duty');
        const shifts = isDeleted ? 0 : (editRow.shifts_count ?? (patientId && patientId !== 0 ? 1 : 0));

        rows.push([
          displayDate, dayOfWeek,
          `"${customerName}"`,
          isDeleted ? '' : `"${editRow.remarks ?? assign?.remarks ?? ''}"`,
          isDeleted ? '' : (editRow.shift_type || assign?.shift_type || '-'),
          isDeleted ? '' : String(Math.round(Number(vals.monthlyScale) || 0)),
          isDeleted ? '' : vals.perHr.toFixed(2),
          isDeleted ? '' : String(vals.hrs),
          String(shifts),
          isDeleted ? '' : vals.salary.toFixed(2),
          isDeleted ? '' : String(vals.approved),
          idx === 0 ? (ded.date || '') : '',
          idx === 0 ? (ded.type || '') : '',
          idx === 0 && ded.amount !== '' ? String(ded.amount) : '',
        ]);
      });
    });

    const csvContent = rows.map(r => r.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    saveAs(blob, `${staffName} - SalarySheet - ${monthLabel} ${selectedYear}.csv`);
  };

  // --- EXPORT: EXCEL ---
  const handleExportExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Salary Sheet');
    
    const monthLabel = months.find(m => m.val === selectedMonth)?.label || '';
    const staffName = staff?.full_name || 'Staff';

    const columns = [
      { header: 'Date', key: 'date', width: 12 },
      { header: 'Week', key: 'week', width: 12 },
      { header: 'Customer Name', key: 'customer', width: 25 }, 
      { header: 'Remarks', key: 'remarks', width: 20 },
      { header: 'Shift Type', key: 'shiftType', width: 15 },
      { header: 'Scale', key: 'scale', width: 12 },
      { header: 'Per Hr', key: 'perHr', width: 10 },
      { header: 'Hrs', key: 'hrs', width: 8 }, 
      { header: 'Shifts', key: 'shifts', width: 8 }, 
      { header: 'Salary', key: 'salary', width: 12 }, 
      { header: 'Approved', key: 'approved', width: 12 }, 
      { header: 'Ded Date', key: 'dedDate', width: 12 },
      { header: 'TID/Type', key: 'dedType', width: 15 },
      { header: 'Amount', key: 'dedAmt', width: 10 }
    ];
    worksheet.columns = columns;

    worksheet.spliceRows(1, 0, [], []); 
    
    worksheet.mergeCells(1, 1, 1, 13);
    const row1 = worksheet.getRow(1);
    row1.height = 30;
    row1.getCell(1).value = `${staffName} - Salary Sheet - ${monthLabel} ${selectedYear}`;
    row1.getCell(1).font = { size: 16, bold: true, color: { argb: 'FF000000' } };
    row1.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };

    for(let i = 1; i <= 13; i++) {
        row1.getCell(i).border = { top: { style: 'thin' }, left: { style: 'thin' }, right: { style: 'thin' }, bottom: { style: 'thin' } };
    }

    const headerRow = worksheet.getRow(3);
    headerRow.height = 25;
    headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1E3A8A' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    });

    getDaysInMonth().forEach(dateObj => {
        const dateStr = formatDate(dateObj); 
        const dayOfWeek = dateObj.toLocaleDateString('en-US', { weekday: 'long' }); 
        const displayDate = `${String(dateObj.getDate()).padStart(2, '0')}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${dateObj.getFullYear()}`;
        
        const matches = assignments.filter(a => dateStr >= a.start_date && dateStr <= a.end_date); 
        const activeMatches = matches.filter(assign => !edits[getRowKey(assign, dateStr)]?.isDeleted);
        
        const visibleMatches = matches.filter(assign => {
            const rowKey = getRowKey(assign, dateStr);
            const editState = edits[rowKey];
            if (editState?.isDeleted && !editState?.isUnsaved) return false;
            return true;
        });
        
        let rows: (Assignment | undefined)[] = [];
        if (visibleMatches.length > 0) rows = visibleMatches; 
        else if (matches.length > 0) rows = [matches[0]]; 
        else rows = [undefined]; 

        const ded = dailyDeductions[dateStr] || { date: '', type: '', amount: '' };
        const startRowNumber = worksheet.rowCount + 1;
        
        rows.forEach((assign, index) => {
            const rowKey = getRowKey(assign, dateStr); 
            const editRow = edits[rowKey] || {}; 
            const isDeleted = !!editRow.isDeleted;
            const status = checkIfModified(assign, rowKey);
            const vals = calculateDailyValues(assign, editRow); 
            
            let customerName = 'No Duty';
            if (isDeleted) {
                customerName = 'No Duty';
            } else if (editRow.patient_id !== undefined && editRow.patient_id !== 0) {
                const p = patients.find(p => p.id === editRow.patient_id);
                customerName = p ? (getClientName(p) || p.patient_name || 'Unnamed') : 'No Duty';
            } else if (assign?.patient && assign.patient.id !== 0) {
                customerName = getClientName(assign.patient) || assign.patient.patient_name || 'No Duty';
            }

            const finalExportDedDate = (ded.amount !== '' || ded.type !== '') ? (ded.date || displayDate) : '';
            let rowShifts: number = 0;
            if (!isDeleted) {
                const currentPatientId = editRow.patient_id !== undefined ? editRow.patient_id : assign?.patient?.id;
                if (currentPatientId && currentPatientId !== 0) {
                    rowShifts = editRow.shifts_count !== undefined ? Number(editRow.shifts_count) : 1;
                }
            }
            const row = worksheet.addRow([ 
                displayDate, 
                dayOfWeek, 
                customerName, 
                isDeleted ? '' : (editRow.remarks ?? assign?.remarks ?? ''), 
                isDeleted ? '' : (editRow.shift_type || assign?.shift_type || '-'), 
                isDeleted ? '' : Math.round(Number(vals.monthlyScale) || 0),
                isDeleted ? '' : parseFloat(vals.perHr.toFixed(2)), 
                isDeleted ? '' : vals.hrs, 
                rowShifts, 
                isDeleted ? '' : parseFloat(vals.salary.toFixed(2)), 
                isDeleted ? '' : vals.approved, 
                index === 0 ? finalExportDedDate : '', 
                index === 0 ? (ded.type || '') : '', 
                index === 0 && ded.amount !== '' ? ded.amount : '' 
            ]);

            if (customerName === 'No Duty') {
                row.getCell(3).font = { color: { argb: 'FFDC2626' }, bold: true };
            }

            let argb: string | undefined = undefined; 
            if (status === 'unsaved') argb = 'FFFFF08A';
            
            row.eachCell((cell) => {
                 cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
                 cell.border = { top: { style:'thin' }, left: { style:'thin' }, bottom: { style:'thin' }, right: { style:'thin' } };
                 if (argb) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb } };
            });

            if (isDeleted) row.getCell(3).font = { color: { argb: 'FFDC2626' }, bold: true };
            if (index === 0 && ded.amount !== '') row.getCell(14).font = { color: { argb: 'FF991B1B' } };
        });

        const endRowNumber = worksheet.rowCount;
        if (endRowNumber > startRowNumber) {
            worksheet.mergeCells(startRowNumber, 1, endRowNumber, 1); 
            worksheet.mergeCells(startRowNumber, 2, endRowNumber, 2); 
            worksheet.mergeCells(startRowNumber, 12, endRowNumber, 12); 
            worksheet.mergeCells(startRowNumber, 13, endRowNumber, 13); 
            worksheet.mergeCells(startRowNumber, 14, endRowNumber, 14); 
        }
    });

    const subRow = worksheet.addRow(['Sub Totals:', '', '', '', '', '', '', '', totals.shiftsSum, totals.salarySum, totals.approvedSum, '', '', totals.deductionsSum]); 
    worksheet.mergeCells(`A${subRow.number}:H${subRow.number}`); 
    subRow.getCell(1).alignment = { horizontal: 'right' }; 
    subRow.eachCell(cell => { 
        cell.font = { bold: true }; 
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFCD34D' } }; 
        cell.border = { top: { style:'thin' }, left: { style:'thin' }, bottom: { style:'thin' }, right: { style:'thin' } };
    });

    const addFooter = (label: string, val: number, bg: string | null = null, isRed = false) => { 
        const r = worksheet.addRow([label]); 
        worksheet.mergeCells(`A${r.number}:I${r.number}`); 
        r.getCell(1).alignment = { horizontal: 'right' }; 
        r.getCell(1).font = { bold: true }; 
        r.getCell(10).value = val; 
        r.getCell(10).font = { bold: true, color: isRed ? { argb: 'FF991B1B' } : undefined }; 
        if (bg) r.getCell(10).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } }; 
        r.eachCell(cell => { cell.border = { top: { style:'thin' }, left: { style:'thin' }, bottom: { style:'thin' }, right: { style:'thin' } }; });
    };
    
    addFooter('Sub Total', Math.round(salarySubtotal));
    addFooter(`HRA (${hraEnabled ? 'Yes' : 'No'})`, hraAmount);
    addFooter(`Allowance (${allowanceEnabled ? 'Yes' : 'No'})`, allowanceAmount);
    addFooter(`Hostel Fees (${hostelEnabled ? 'Yes' : 'No'})`, hostelAmount, null, true); 
    addFooter('Gross Salary', grossSalary, 'FFBFDBFE');    
    addFooter(`Professional Tax (${ptEnabled ? 'Yes' : 'No'})`, ptAmount, null, true); 
    addFooter(`TDS (${tdsEnabled ? tdsPercent + '%' : 'No'})`, Math.round(tdsAmount), null, true); 
    addFooter('Advances (From Sheet)', Math.round(advances), null, true);
    addFooter('Total Deductions', Math.round(totals.deductionsSum + ptAmount + tdsAmount), null, true); 
    addFooter('Net Take Home Salary', Math.round(netTakeHome), 'FF86EFAC');
    
    const buffer = await workbook.xlsx.writeBuffer(); 
    const fileName = `${staffName} - SalarySheet - ${monthLabel} ${selectedYear}.xlsx`;
    saveAs(new Blob([buffer]), fileName);
  };

  // --- STYLES ---
  const styles: { [key: string]: React.CSSProperties } = {
    page: { padding: '0.75rem 0.6rem', backgroundColor: '#f8fafc', minHeight: '100vh', fontFamily: '"Inter", sans-serif' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', gap: '1rem', flexWrap: 'nowrap' },
    title: { fontSize: '1.15rem', fontWeight: 700, color: '#0f172a', margin: 0, lineHeight: 1.2 },
    subtitle: { color: '#64748b', fontSize: '0.72rem', marginTop: '0.1rem', textTransform: 'capitalize' },
    backBtn: { display: 'flex', alignItems: 'center', gap: '0.5rem', border: 'none', background: 'transparent', color: '#64748b', cursor: 'pointer', marginBottom: '1rem', fontSize: '0.9rem', fontWeight: 500 },
    controlsGroup: { display: 'flex', gap: '0.5rem', alignItems: 'center', backgroundColor: 'white', padding: '0.3rem', borderRadius: '10px', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', flexShrink: 0 },
    datePickerContainer: { display: 'flex', alignItems: 'center', backgroundColor: '#f1f5f9', borderRadius: '8px', padding: '0.2rem', marginRight:'0.5rem' },
    pickerBtn: { display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0.8rem', backgroundColor: 'transparent', border: 'none', fontSize: '0.85rem', fontWeight: 600, color: '#334155', cursor: 'pointer', borderRadius: '6px', outline: 'none', minWidth: '100px', justifyContent: 'space-between' },
    divider: { width: '1px', height: '16px', backgroundColor: '#cbd5e1', margin: '0 2px' },
    dropdownMenu: { position: 'absolute', top: 'calc(100% + 8px)', right: 0, backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '10px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', width: '150px', maxHeight: '300px', overflowY: 'auto', zIndex: 50, padding: '0.3rem' },
    dropdownItem: { padding: '0.5rem 0.7rem', fontSize: '0.9rem', color: '#475569', cursor: 'pointer', borderRadius: '6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', transition: 'background 0.1s' },
    activeItem: { backgroundColor: '#eff6ff', color: '#2563eb', fontWeight: 600 },
    saveBtn: { backgroundColor: '#1e3a8a', color: 'white', border: 'none', padding: '0.6rem 1.2rem', borderRadius: '8px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', marginLeft: '0.5rem' },
    csvBtn: { backgroundColor: '#64748b', color: 'white', border: 'none', padding: '0.6rem 1rem', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' },
    excelBtn: { backgroundColor: '#10b981', color: 'white', border: 'none', padding: '0.6rem 1rem', borderRadius: '8px', fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' },
    // Breathing room on both sides, so the sheet reads as centred rather than
    // pinned to an edge. "0 auto" splits the maxWidth reduction evenly, which is
    // what keeps the two gaps identical at any window width.
    //
    // 213px = the original 60px, plus the 153px given up by the two columns that
    // were removed (Approved 61px, Ded Date 92px). That width becomes margin
    // instead of being absorbed by the remaining columns, so every other column
    // stays exactly as wide as it was.
    tableCard: { backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', border: '1px solid #cbd5e1', overflow: 'hidden', maxWidth: 'calc(100% - 213px)', margin: '0 auto' },
    tableWrapper: { overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem', textAlign: 'center', color: '#334155' },
    thead: { backgroundColor: '#1e3a8a', borderBottom: '1px solid #172554' },
    th: { padding: '0.35rem 0.2rem', color: 'white', fontWeight: 600, textTransform: 'uppercase', border: '1px solid #475569', whiteSpace: 'nowrap', fontSize: '0.7rem' },
    td: { padding: '0', border: '1px solid #cbd5e1', verticalAlign: 'middle', height: '27px' },
    input: { width: '100%', height: '100%', border: 'none', textAlign: 'center', background: 'transparent', outline: 'none', fontSize: '0.82rem', color: '#0f172a', padding: '0 2px', fontFamily: 'inherit' },
    footerLabel: { backgroundColor: '#f8fafc', fontWeight: 700, textAlign: 'right', paddingRight: '1rem', borderTop:'1px solid #e2e8f0', color: '#475569' },
    footerValue: { backgroundColor: 'white', fontWeight: 700, color: '#1e3a8a', border:'1px solid #e2e8f0', position: 'relative' },
    editableInput: { width: '100%', height: '100%', border: 'none', textAlign: 'center', fontWeight: 700, color: '#1e3a8a', background: 'transparent', outline: 'none', fontSize: '0.85rem' },
    subtotalRow: { backgroundColor: '#fcd34d', fontWeight: 700 }, 
    deductionHeader: { backgroundColor: '#fef2f2', color: '#991b1b', fontWeight: 700, borderBottom: '1px solid #fca5a5' },
    select: { border: '1px solid #cbd5e1', borderRadius: '4px', padding: '2px 4px', fontSize: '0.75rem', fontWeight: 600, color: '#0f172a', cursor: 'pointer', outline: 'none', marginRight: '0.5rem' },
    sheetRow: { display: 'flex', alignItems: 'stretch' },
    summaryPanel: { width: '260px', flexShrink: 0, borderLeft: '2px solid #cbd5e1', display: 'flex', flexDirection: 'column', backgroundColor: '#f8fafc' },
    sumRow: { display: 'flex', alignItems: 'center', gap: '6px', padding: '0 10px', height: '32px', borderBottom: '1px solid #e2e8f0', fontSize: '0.78rem', whiteSpace: 'nowrap' },
    sumLabel: { fontWeight: 600, color: '#475569', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' },
    sumSelect: { border: '1px solid #cbd5e1', borderRadius: '4px', padding: '1px 2px', fontSize: '0.7rem', fontWeight: 600, color: '#0f172a', cursor: 'pointer', outline: 'none' },
    sumInput: { width: '58px', border: '1px solid #cbd5e1', borderRadius: '4px', textAlign: 'right', fontWeight: 700, color: '#1e3a8a', outline: 'none', fontSize: '0.78rem', padding: '2px 4px' },
    sumVal: { fontWeight: 700, color: '#1e3a8a', minWidth: '60px', textAlign: 'right' },
    headStat: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', padding: '0.28rem 0.5rem', borderRadius: '8px', backgroundColor: 'white', border: '1px solid #e2e8f0', boxShadow: '0 1px 2px rgba(0,0,0,0.04)', minWidth: '78px', whiteSpace: 'nowrap' },
    headStatLabel: { fontSize: '0.58rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' },
    headStatValue: { fontSize: '0.9rem', fontWeight: 700, lineHeight: 1.2 },
    // Bank details, under the take-home figure — read-only, straight from the
    // registration form. Values wrap rather than being clipped: an account
    // number that is cut off is worse than useless on a payment screen.
    // marginTop leaves a band of the panel's own background between the green
    // Net Take Home row and this block, so the bank details read as a separate
    // section rather than as one more footer line.
    bankHeader: { display: 'flex', alignItems: 'center', gap: '6px', marginTop: '30px', padding: '5px 6px 5px 10px', minHeight: '26px', fontSize: '0.66rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: '#475569', backgroundColor: '#e2e8f0', borderTop: '2px solid #cbd5e1', borderBottom: '1px solid #cbd5e1' },
    bankBtn: { border: '1px solid #cbd5e1', background: '#fff', color: '#475569', borderRadius: '4px', padding: '1px 7px', fontSize: '0.64rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', textTransform: 'uppercase', letterSpacing: '0.03em' },
    bankBtnPrimary: { border: 'none', background: '#1e3a8a', color: '#fff', borderRadius: '4px', padding: '2px 8px', fontSize: '0.64rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', textTransform: 'uppercase', letterSpacing: '0.03em' },
    bankInput: { width: '100%', border: '1px solid #cbd5e1', borderRadius: '4px', padding: '2px 5px', fontSize: '0.76rem', fontWeight: 600, color: '#0f172a', outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box' },
    bankRow: { display: 'flex', flexDirection: 'column', padding: '4px 10px', borderBottom: '1px solid #f1f5f9', backgroundColor: '#ffffff' },
    bankLabel: { fontSize: '0.6rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.03em', lineHeight: 1.4 },
    bankValue: { fontSize: '0.76rem', fontWeight: 600, color: '#0f172a', overflowWrap: 'anywhere', lineHeight: 1.35 }
  };

  const getRowBackground = (status: string, assign: Assignment | undefined, editRow: any) => {
    // "Unsaved" is an operational warning (this row hasn't been saved yet) —
    // it stays on top of a custom highlight so it's never accidentally hidden.
    if (status === 'unsaved') return '#fef08a';
    const rowColor = editRow?.row_color !== undefined ? editRow.row_color : assign?.row_color;
    return rowColor || 'white';
  };

  // Options for a Bank / Branch dropdown: the shared reference list, plus the
  // value already on the record if it was never added to that list — otherwise
  // opening the dropdown would silently drop what the nurse's record says.
  const referenceItems = (category: string, current?: string) => {
    const opts = refOptions[category] || [];
    const items = opts.map((o: ReferenceOption) => ({ id: o.label, name: o.label }));
    const value = (current || '').trim();
    if (value && !opts.some((o: ReferenceOption) => o.label === value)) items.push({ id: value, name: value });
    return items;
  };

  const bankFields = BANK_FIELDS.map(f => ({
    ...f,
    value: ((staff as any)?.[f.key] || '').trim() as string,
  }));
  const hasBankDetails = bankFields.some(f => f.value);
  // Physio records carry the same columns, but their only PATCH is multipart and
  // rebuilds the education rows — not safe to drive from here. Until a physio
  // inline endpoint exists, those sheets stay read-only.
  const canEditBank = staffType !== 'physio';

  const startBankEdit = () => {
    setBankDraft(Object.fromEntries(BANK_FIELDS.map(f => [f.key, (staff as any)?.[f.key] || ''])));
    setBankEditing(true);
  };

  const saveBankDetails = async () => {
    if (!staffId) return;
    setBankSaving(true);
    try {
      // Every field goes in each time: a box the user emptied has to be sent as
      // an explicit null, or the endpoint's exclude_unset would leave the old
      // value sitting in the column.
      const payload = Object.fromEntries(
        BANK_FIELDS.map(f => [f.key, (bankDraft[f.key] || '').trim() || null])
      );
      const res = await axios.patch(`${API_BASE_URL}/api/nurses/${staffId}/inline`, payload, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` },
      });
      setStaff(res.data);          // the endpoint returns the whole nurse
      setBankEditing(false);
      toast.success('Bank details saved');
    } catch (err: any) {
      // Stay in edit mode — dropping back to the read view would throw away
      // whatever they had typed.
      toast.error(err?.response?.data?.detail || 'Failed to save bank details');
    } finally {
      setBankSaving(false);
    }
  };

  const patientOptions = [ { label: 'No Duty', value: 0 }, ...patients.map(p => ({ label: getClientName(p) || p.patient_name || 'Unnamed', value: p.id })) ];
  const standardShiftOptions = [ "08 Morning", "08 Afternoon", "08 Night", "12 Day", "12 Night", "24 Hrs", "08 Morning - Junior", "08 Afternoon - Junior", "08 Night - Junior", "12 Day - Junior", "12 Night - Junior", "24 Hrs Junior" ];

  // Sum of just the currently drag-selected range within a summable column —
  // separate from Sub Total/Gross Salary, which always cover the whole month.
  // Accumulated while the table body below renders (it has the per-row perHr/
  // hrs/salary values already computed right there), then read once after —
  // simpler and guaranteed consistent with what's on screen, vs. re-deriving
  // each value a second time from scratch.
  const SUMMABLE_FIELDS = ['perHr', 'actual_hours', 'shifts_count', 'admin_approved_amount', 'ded_amount'];
  let selSum = 0;
  let selCount = 0;
  const accumulateSelected = (field: string, key: string, value: number) => {
    if (selectedColumn === field && selectedCells.has(key)) { selSum += value; selCount++; }
  };

  return (
    <div style={styles.page}>
      <style>{bankCellCss}</style>
      <header style={styles.header}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'nowrap', flex: '1 1 auto', minWidth: 0 }}>
          <button style={{...styles.backBtn, marginBottom: 0, flexShrink: 0}} onClick={() => navigate('/salarysheet')}><ArrowLeft size={18} /> Back to Salary Sheets</button>
          <div style={{ minWidth: 0, flexShrink: 1, overflow: 'hidden' }}>
            <h1 style={{...styles.title, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{staff ? staff.full_name : 'Loading...'}</h1>
            <p style={{...styles.subtitle, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{staff?.profile} • Salary Sheet for {months.find(m => m.val === selectedMonth)?.label} {selectedYear}</p>
          </div>
          {/* Compact totals next to the name — single row, never wraps down. */}
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'nowrap', flexShrink: 0 }}>
            <div style={styles.headStat}>
              <span style={styles.headStatLabel}>Gross Salary</span>
              <span style={{ ...styles.headStatValue, color: '#0f172a' }}>₹ {Math.round(grossSalary).toLocaleString()}</span>
            </div>
            <div style={styles.headStat}>
              <span style={styles.headStatLabel}>TDS</span>
              <span style={{ ...styles.headStatValue, color: '#dc2626' }}>₹ {Math.round(tdsAmount).toLocaleString()}</span>
            </div>
            <div style={styles.headStat}>
              <span style={styles.headStatLabel}>P.T</span>
              <span style={{ ...styles.headStatValue, color: '#dc2626' }}>₹ {Math.round(ptAmount).toLocaleString()}</span>
            </div>
            <div style={styles.headStat}>
              <span style={styles.headStatLabel}>Advances</span>
              <span style={{ ...styles.headStatValue, color: '#ea580c' }}>₹ {totals.deductionsSum.toLocaleString()}</span>
            </div>
            <div style={styles.headStat}>
              <span style={styles.headStatLabel}>Total Deduction</span>
              <span style={{ ...styles.headStatValue, color: '#991b1b' }}>₹ {Math.round(finalTotalDeductions).toLocaleString()}</span>
            </div>
            <div style={styles.headStat}>
              <span style={styles.headStatLabel}>Net Salary</span>
              <span style={{ ...styles.headStatValue, color: '#16a34a' }}>₹ {Math.round(netTakeHome).toLocaleString()}</span>
            </div>
          </div>
        </div>
        <div style={styles.controlsGroup}>
            <div style={styles.datePickerContainer}>
                <div style={{position: 'relative'}} ref={monthRef}>
                    <button style={styles.pickerBtn} onClick={() => { setIsMonthOpen(!isMonthOpen); setIsYearOpen(false); }}>
                        <div style={{display:'flex', alignItems:'center', gap:'0.5rem'}}><Calendar size={15} color="#3b82f6" /><span>{months.find(m => m.val === selectedMonth)?.label}</span></div><ChevronDown size={14} color="#94a3b8"/>
                    </button>
                    {isMonthOpen && <div style={styles.dropdownMenu}>{months.map((m) => <div key={m.val} style={{...styles.dropdownItem, ...(m.val === selectedMonth ? styles.activeItem : {})}} onClick={() => { setSelectedMonth(m.val); setIsMonthOpen(false); }}>{m.label}</div>)}</div>}
                </div>
                <div style={styles.divider}></div>
                <div style={{position: 'relative'}} ref={yearRef}>
                    <button style={{...styles.pickerBtn, minWidth: '80px'}} onClick={() => { setIsYearOpen(!isYearOpen); setIsMonthOpen(false); }}><span>{selectedYear}</span><ChevronDown size={14} color="#94a3b8"/></button>
                    {isYearOpen && <div style={{...styles.dropdownMenu, width:'100px'}}>{years.map((y) => <div key={y} style={{...styles.dropdownItem, ...(y === selectedYear ? styles.activeItem : {})}} onClick={() => { setSelectedYear(y); setIsYearOpen(false); }}>{y}</div>)}</div>}
                </div>
            </div>
            <button style={styles.csvBtn} onClick={handleExportCSV}><Download size={16} /> CSV</button><button style={styles.excelBtn} onClick={handleExportExcel}><FileText size={16} /> Excel</button>
            <button style={styles.saveBtn} onClick={handleSave} disabled={saving}>{saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save Changes</button>
        </div>
      </header>


      <div style={styles.tableCard}>
        <div style={styles.sheetRow}>
          <div style={{...styles.tableWrapper, flex: 1, minWidth: 0}}>
          <table ref={tableRef} style={styles.table}>
            <thead style={styles.thead}>
              <tr>
                <th style={{...styles.th, width:'104px'}}>Date</th><th style={{...styles.th, width:'80px'}}>Week</th><th style={{...styles.th, width:'40px'}}>Act</th>
                <th style={{...styles.th, width:'170px', textAlign: 'left'}}>Customer Name</th><th onClick={() => pickColumn('remarks')} title="Click to select this column, then type a value in any cell to fill every row (Esc clears)" style={{...styles.th, width:'90px', cursor:'pointer', ...(selectedColumn === 'remarks' ? {outline:'3px solid #fcd34d', outlineOffset:'-3px'} : {})}}>Remarks (Edit)</th><th onClick={() => pickColumn('shift_type')} title="Click to select this column, then type a value in any cell to fill every row (Esc clears)" style={{...styles.th, width:'80px', cursor:'pointer', ...(selectedColumn === 'shift_type' ? {outline:'3px solid #fcd34d', outlineOffset:'-3px'} : {})}}>Shift Type</th>
                <th onClick={() => pickColumn('monthlyScale')} title="Click to select this column, then type a value in any cell to fill every row (Esc clears)" style={{...styles.th, width:'70px', cursor:'pointer', ...(selectedColumn === 'monthlyScale' ? {outline:'3px solid #fcd34d', outlineOffset:'-3px'} : {})}}>Scale</th><th style={{...styles.th, width:'55px'}}>Per Hr</th><th style={{...styles.th, width:'50px'}}>Hrs</th>
                <th style={{...styles.th, width:'50px'}}>Shifts</th>
                <th onClick={() => pickColumn('admin_approved_amount')} title="Click to select this column, then type a value in any cell to fill every row (Esc clears)" style={{...styles.th, width:'70px', cursor:'pointer', ...(selectedColumn === 'admin_approved_amount' ? {outline:'3px solid #fcd34d', outlineOffset:'-3px'} : {})}}>Salary</th>
                <th onClick={() => pickColumn('ded_type')} title="Click to select this column, then type a value in any cell to fill every row (Esc clears)" style={{...styles.th, ...styles.deductionHeader, width:'72px', cursor:'pointer', ...(selectedColumn === 'ded_type' ? {outline:'3px solid #fcd34d', outlineOffset:'-3px'} : {})}}>TID/Type</th><th onClick={() => pickColumn('ded_amount')} title="Click to select this column, then type a value in any cell to fill every row (Esc clears)" style={{...styles.th, ...styles.deductionHeader, width:'64px', cursor:'pointer', ...(selectedColumn === 'ded_amount' ? {outline:'3px solid #fcd34d', outlineOffset:'-3px'} : {})}}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {loading ? ( <tr><td colSpan={13} style={{padding: '3rem', color: '#64748b'}}>Loading...</td></tr> ) : (
                  getDaysInMonth().map((dateObj) => {
                    const dateStr = formatDate(dateObj); 
                    const dayOfWeek = dateObj.toLocaleDateString('en-US', { weekday: 'long' }); 
                    const displayDate = `${String(dateObj.getDate()).padStart(2, '0')}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${dateObj.getFullYear()}`;
                    
                    const matches = assignments.filter(a => dateStr >= a.start_date && dateStr <= a.end_date); 
                    const visibleMatches = matches.filter(assign => {
                        const rowKey = getRowKey(assign, dateStr);
                        const editState = edits[rowKey];
                        if (editState?.isDeleted && !editState?.isUnsaved) return false;
                        return true;
                    });

                    let rowsToRender = [];
                    if (visibleMatches.length > 0) {
                        rowsToRender = visibleMatches;
                    } else if (matches.length > 0) {
                        rowsToRender = [matches[0]]; 
                    } else {
                        rowsToRender = [undefined]; 
                    }

                    const ded = dailyDeductions[dateStr] || { date: '', type: '', amount: '' };
                    accumulateSelected('ded_amount', dateStr, Number(ded.amount) || 0);

                    return rowsToRender.map((assign, index) => {
                      const rowKey = getRowKey(assign, dateStr); 
                      const editRow = edits[rowKey] || {}; 
                      const isReplaced = !!editRow.isDeleted;
                      const effectiveAssign = isReplaced ? undefined : assign; 
                      const rowStatus = checkIfModified(effectiveAssign, rowKey);
                      const { monthlyScale, hrs, perHr, salary, approved, currentShiftType } = calculateDailyValues(effectiveAssign, editRow);
                      const isFirstRow = index === 0; 
                      const rowSpan = rowsToRender.length; 
                      const currentPatientId = editRow.patient_id !== undefined ? editRow.patient_id : effectiveAssign?.patient?.id;
                      if (!isReplaced) {
                        accumulateSelected('perHr', rowKey, perHr);
                        accumulateSelected('actual_hours', rowKey, Number(hrs) || 0);
                        accumulateSelected('shifts_count', rowKey, editRow.shifts_count !== undefined ? Number(editRow.shifts_count) : (!currentPatientId ? 0 : 1));
                        // Same value the Salary cell itself displays/edits — not the raw
                        // calculated `salary`, which a typed-over admin_approved_amount replaces.
                        accumulateSelected('admin_approved_amount', rowKey, editRow.admin_approved_amount !== undefined ? Number(editRow.admin_approved_amount) : (approved > 0 ? approved : 0));
                      }

                      return (
                        <tr key={rowKey}
                          onMouseDown={(e) => startDrag(rowKey, e)}
                          onMouseEnter={() => extendDrag(rowKey)}
                          style={{ backgroundColor: selectedRows.has(rowKey) ? '#dbeafe' : getRowBackground(rowStatus, assign, editRow), transition: 'background 0.1s', cursor: isReplaced ? 'default' : 'cell', userSelect: dragging.current ? 'none' : 'auto' }}>
                          
                          {isFirstRow && (
                              <>
                                  <td rowSpan={rowSpan} style={{...styles.td, color: '#64748b'}}>
                                    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', whiteSpace: 'nowrap'}}>
                                      {displayDate}
                                      <button onClick={() => handleAddShift(dateStr)} title="Add Shift" style={{ border: 'none', background: '#eff6ff', color: '#2563eb', cursor: 'pointer', borderRadius: '4px', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                        <Plus size={14} />
                                      </button>
                                    </div>
                                  </td>
                                  <td rowSpan={rowSpan} style={{...styles.td, color: '#334155', fontWeight: 400}}>{dayOfWeek}</td>
                              </>
                          )}

                          <td style={styles.td}>
                              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px' }}>
                                  {!isReplaced && assign && ( <button onClick={() => handleRemoveShift(rowKey, assign)} title="Delete Shift" style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: '#94a3b8' }}><Trash2 size={14} /></button> )}
                              </div>
                          </td>

                          {isReplaced ? ( 
                              <>
                                  <td style={{...styles.td, textAlign: 'left', paddingLeft: '8px', color: '#dc2626', fontWeight: 700}}>No Duty</td>
                                  <td style={styles.td}>-</td>
                                  <td style={styles.td}>-</td>
                                  <td style={styles.td}>0</td>
                                  <td style={styles.td}>-</td>
                                  <td style={styles.td}>-</td>
                                  <td style={styles.td}>
                                      <input type="number" value={0} disabled style={{...styles.input, fontWeight: 600, color: '#94a3b8'}} />
                                  </td>
                                  <td style={styles.td}>-</td>
                              </>
                          ) : (
                              <>
                                  <td style={{...styles.td, textAlign: 'left'}}><SearchableDropdown options={patientOptions} value={currentPatientId || 0} onChange={(val) => handleInputChange(rowKey, 'patient_id', val)} placeholder="Select Customer..." /></td>
                                  <td onMouseDown={(e) => startCellSel('remarks', rowKey, e)} onMouseEnter={() => extendCellSel('remarks', rowKey)} style={{...styles.td, ...((selectedColumn === 'remarks' && selectedCells.has(rowKey)) ? {backgroundColor:'#bfdbfe'} : {})}}><input type="text" value={editRow.remarks ?? effectiveAssign?.remarks ?? ''} onChange={(e) => handleInputChange(rowKey, 'remarks', e.target.value)} style={{...styles.input, ...((editRow?.remarks_color !== undefined ? editRow.remarks_color : effectiveAssign?.remarks_color) ? { color: (editRow?.remarks_color !== undefined ? editRow.remarks_color : effectiveAssign?.remarks_color), fontWeight: 700 } : {})}} placeholder="-"/></td>
                                  <td onMouseDown={(e) => startCellSel('shift_type', rowKey, e)} onMouseEnter={() => extendCellSel('shift_type', rowKey)} style={{...styles.td, ...((selectedColumn === 'shift_type' && selectedCells.has(rowKey)) ? {backgroundColor:'#bfdbfe'} : {})}}><select value={currentShiftType} onChange={(e) => handleInputChange(rowKey, 'shift_type', e.target.value)} style={{...styles.input, cursor: 'pointer', appearance: 'auto', fontWeight: 600, color: '#334155'}}><option value="">-</option>{standardShiftOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}</select></td>
                                  <td onMouseDown={(e) => startCellSel('monthlyScale', rowKey, e)} onMouseEnter={() => extendCellSel('monthlyScale', rowKey)} style={{...styles.td, ...((selectedColumn === 'monthlyScale' && selectedCells.has(rowKey)) ? {backgroundColor:'#bfdbfe'} : {})}}><input type="number" value={editRow.monthlyScale !== undefined ? editRow.monthlyScale : (Number(monthlyScale) > 0 ? Math.round(Number(monthlyScale)) : '')} onChange={(e) => handleInputChange(rowKey, 'monthlyScale', e.target.value)} style={styles.input} placeholder="0" /></td>
                                  <td onMouseDown={(e) => startCellSel('perHr', rowKey, e)} onMouseEnter={() => extendCellSel('perHr', rowKey)} style={{...styles.td, ...((selectedColumn === 'perHr' && selectedCells.has(rowKey)) ? {backgroundColor:'#bfdbfe'} : {})}}>{perHr > 0 ? perHr.toFixed(2) : '-'}</td>
                                  <td onMouseDown={(e) => startCellSel('actual_hours', rowKey, e)} onMouseEnter={() => extendCellSel('actual_hours', rowKey)} style={{...styles.td, ...((selectedColumn === 'actual_hours' && selectedCells.has(rowKey)) ? {backgroundColor:'#bfdbfe'} : {})}}>
                                    <input
                                      type="number"
                                      value={hrs}
                                      onChange={(e) => handleInputChange(rowKey, 'actual_hours', e.target.value)}
                                      style={{...styles.input, fontWeight: 600}}
                                    />
                                  </td>
                                  
                                  <td onMouseDown={(e) => startCellSel('shifts_count', rowKey, e)} onMouseEnter={() => extendCellSel('shifts_count', rowKey)} style={{...styles.td, ...((selectedColumn === 'shifts_count' && selectedCells.has(rowKey)) ? {backgroundColor:'#bfdbfe'} : {})}}>
                                      <input
                                          type="number" step="0.5"
                                          value={editRow.shifts_count !== undefined ? editRow.shifts_count : (!currentPatientId ? 0 : 1)}
                                          onChange={(e) => handleInputChange(rowKey, 'shifts_count', e.target.value === '' ? '' : Number(e.target.value))}
                                          style={{...styles.input, fontWeight: 600, color: (editRow.shifts_count !== undefined ? editRow.shifts_count : (!currentPatientId ? 0 : 1)) === 0 ? '#94a3b8' : '#0f172a'}}
                                      />
                                  </td>
                                  
                                  {/* Salary is the payable figure and is typed over directly: it
                                      starts at the calculated amount (Per Hr × Hrs, or the rate
                                      the assignment was booked at) and holds whatever is typed
                                      instead. It is what the Sub Total and Gross Salary read, and
                                      is still stored as admin_approved_amount — the same field the
                                      old Approved column wrote to, so saved sheets are unaffected.
                                      Transparent while selected so the row's highlight shows
                                      through — this is one of the columns the selection fills. */}
                                  <td
                                    title={salary > 0 ? `Calculated: ${salary.toFixed(2)}` : undefined}
                                    onMouseDown={(e) => startCellSel('admin_approved_amount', rowKey, e)}
                                    onMouseEnter={() => extendCellSel('admin_approved_amount', rowKey)}
                                    style={{...styles.td, backgroundColor: (selectedColumn === 'admin_approved_amount' && selectedCells.has(rowKey)) ? '#bfdbfe' : (selectedRows.has(rowKey) ? 'transparent' : (effectiveAssign && !effectiveAssign.isTemp && rowStatus === 'clean' ? '#f0fdf4' : 'transparent'))}}
                                  >
                                    <input type="number" value={editRow.admin_approved_amount !== undefined ? editRow.admin_approved_amount : (approved > 0 ? approved : '')} onChange={(e) => handleInputChange(rowKey, 'admin_approved_amount', e.target.value)} style={{...styles.input, fontWeight: 700, color:'#15803d'}} placeholder="0" />
                                  </td>
                              </>
                          )}

                          {isFirstRow && (
                            <>
                                {/* The deduction's own date column is gone: a deduction is
                                    entered on its day's row, so the row's date is the date.
                                    A date already stored against an older deduction is left
                                    as it is on save rather than being overwritten. */}
                                <td rowSpan={rowSpan} onMouseDown={(e) => startCellSel('ded_type', dateStr, e)} onMouseEnter={() => extendCellSel('ded_type', dateStr)} style={{...styles.td, borderLeft: '2px solid #cbd5e1', backgroundColor: (selectedColumn === 'ded_type' && selectedCells.has(dateStr)) ? '#bfdbfe' : '#ffffff'}}><input type="text" value={ded.type} onChange={e => handleDeductionChange(dateStr, 'type', e.target.value)} style={styles.input} placeholder="-" /></td>
                                <td rowSpan={rowSpan} onMouseDown={(e) => startCellSel('ded_amount', dateStr, e)} onMouseEnter={() => extendCellSel('ded_amount', dateStr)} style={{...styles.td, backgroundColor: (selectedColumn === 'ded_amount' && selectedCells.has(dateStr)) ? '#bfdbfe' : '#ffffff'}}><input type="number" value={ded.amount} onChange={e => handleDeductionChange(dateStr, 'amount', e.target.value)} style={styles.input} placeholder="0" /></td>
                            </>
                          )}
                        </tr>
                      );
                    });
                  })
              )}
              
              <tr style={styles.subtotalRow}>
                  <td colSpan={9} style={{...styles.td, textAlign: 'right', paddingRight: '1rem'}}>Sub Totals:</td>
                  <td style={styles.td}>{totals.shiftsSum}</td>
                  {/* Sums the payable column, which is what the salary column now holds. */}
                  <td style={styles.td}>₹ {totals.approvedSum.toLocaleString()}</td>
                  <td style={{...styles.td, textAlign: 'right', paddingRight: '1rem'}}>Sub Total Deductions:</td><td style={{...styles.td, color: '#991b1b'}}>₹ {totals.deductionsSum.toLocaleString()}</td>
              </tr>

            </tbody>
          </table>
          </div>

          {/* Salary summary moved beside the table (was a stack of full-width rows
              below it) so all day rows fit without vertical scroll. */}
          <div style={styles.summaryPanel}>
            <StaffPhoto path={staff?.photo_url} name={staff?.full_name || ''} />
            {/* Sub Total — the sum of the Salary column, typed over when it needs
                to be. Clearing the box (or leaving it equal to the calculated
                sum) returns it to tracking the column, so an override is never
                sticky by accident. */}
            <style>{`.salary-subtotal-input::placeholder{color:#451a03;opacity:1;}`}</style>
            <div style={{...styles.sumRow, backgroundColor: '#fcd34d', borderBottom: '2px solid #d97706'}}>
              <span style={{...styles.sumLabel, color: '#451a03', fontWeight: 800}} title="Sum of the Salary column — type over it to override">Sub Total</span>
              <input
                type="number"
                className="salary-subtotal-input"
                value={subtotalDraft ?? subtotalOverride ?? Math.round(totals.approvedSum)}
                onFocus={(e) => { setSubtotalDraft(String(subtotalOverride ?? Math.round(totals.approvedSum))); e.target.select(); }}
                onChange={(e) => { setSubtotalDraft(e.target.value); setSubtotalOverride(e.target.value === '' ? null : Number(e.target.value)); }}
                onBlur={() => { setSubtotalDraft(null); if (subtotalOverride !== null && subtotalOverride === Math.round(totals.approvedSum)) setSubtotalOverride(null); }}
                style={{...styles.sumInput, color: subtotalOverride !== null ? '#b45309' : '#1e3a8a'}}
                placeholder={Math.round(totals.approvedSum).toString()}
                title={subtotalOverride !== null ? `Overridden — calculated total is ₹ ${Math.round(totals.approvedSum).toLocaleString()}. Clear to go back to it.` : undefined}
              />
            </div>
            <div style={styles.sumRow}>
              <span style={styles.sumLabel}>HRA</span>
              <select value={hraEnabled ? "Yes" : "No"} onChange={(e) => setHraEnabled(e.target.value === "Yes")} style={styles.sumSelect}><option value="Yes">Yes</option><option value="No">No</option></select>
              {hraEnabled ? <input type="number" value={hraValue === 0 ? '' : hraValue} onChange={(e) => setHraValue(e.target.value === '' ? 0 : Number(e.target.value))} style={styles.sumInput} placeholder="0"/> : <span style={styles.sumVal}>₹ 0</span>}
            </div>
            <div style={styles.sumRow}>
              <span style={styles.sumLabel}>Allowance</span>
              <select value={allowanceEnabled ? "Yes" : "No"} onChange={(e) => setAllowanceEnabled(e.target.value === "Yes")} style={styles.sumSelect}><option value="Yes">Yes</option><option value="No">No</option></select>
              {allowanceEnabled ? <input type="number" value={allowanceValue === 0 ? '' : allowanceValue} onChange={(e) => setAllowanceValue(e.target.value === '' ? 0 : Number(e.target.value))} style={styles.sumInput} placeholder="0"/> : <span style={styles.sumVal}>₹ 0</span>}
            </div>
            <div style={styles.sumRow}>
              <span style={styles.sumLabel}>Hostel Fees</span>
              <select value={hostelEnabled ? "Yes" : "No"} onChange={(e) => { const on = e.target.value === "Yes"; setHostelEnabled(on); if (on && hostelValue === 0) setHostelValue(2000); }} style={styles.sumSelect}><option value="Yes">Yes</option><option value="No">No</option></select>
              {hostelEnabled ? <input type="number" value={hostelValue === 0 ? '' : hostelValue} onChange={(e) => setHostelValue(e.target.value === '' ? 0 : Number(e.target.value))} style={{...styles.sumInput, color: '#991b1b'}} placeholder="0"/> : <span style={{...styles.sumVal, color: '#991b1b'}}>- ₹ 0</span>}
            </div>
            <div style={{...styles.sumRow, backgroundColor: '#bfdbfe'}}>
              <span style={styles.sumLabel} title="Sub Total + HRA + Allowance - Hostel Fees">Gross Salary</span>
              <span style={{...styles.sumVal, color: '#1e3a8a'}}>₹ {grossSalary.toLocaleString()}</span>
            </div>
            <div style={styles.sumRow}>
              <span style={styles.sumLabel}>Professional Tax</span>
              <select value={ptEnabled ? "Yes" : "No"} onChange={(e) => setPtEnabled(e.target.value === "Yes")} style={styles.sumSelect}><option value="Yes">Yes</option><option value="No">No</option></select>
              {ptEnabled ? <input type="number" value={ptValue === 0 ? '' : ptValue} onChange={(e) => setPtValue(e.target.value === '' ? 0 : Number(e.target.value))} style={{...styles.sumInput, color: '#991b1b'}} placeholder="0"/> : <span style={{...styles.sumVal, color: '#991b1b'}}>- ₹ 0</span>}
            </div>
            <div style={styles.sumRow}>
              <span style={styles.sumLabel}>TDS</span>
              <select value={tdsEnabled ? "Yes" : "No"} onChange={(e) => { const on = e.target.value === "Yes"; setTdsEnabled(on); if (on && tdsPercent === 0) setTdsPercent(10); }} style={styles.sumSelect}><option value="Yes">Yes</option><option value="No">No</option></select>
              {tdsEnabled && <input type="number" min="0" max="100" value={tdsPercent === 0 ? '' : tdsPercent} onChange={(e) => setTdsPercent(e.target.value === '' ? 0 : Number(e.target.value))} style={{width: '34px', padding: '2px', textAlign: 'center', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.72rem'}} placeholder="%"/>}
              <span style={{...styles.sumVal, color: '#991b1b'}}>₹ {Math.round(tdsAmount).toLocaleString()}</span>
            </div>
            <div style={styles.sumRow}>
              <span style={styles.sumLabel}>Advances</span>
              <span style={{...styles.sumVal, color: '#991b1b'}}>₹ {advances.toLocaleString()}</span>
            </div>
            <div style={styles.sumRow}>
              <span style={styles.sumLabel}>Total Deductions</span>
              <span style={{...styles.sumVal, color: '#991b1b'}}>₹ {Math.round(finalTotalDeductions).toLocaleString()}</span>
            </div>
            <div style={{...styles.sumRow, backgroundColor: '#86efac', borderBottom: 'none'}}>
              <span style={{...styles.sumLabel, color: '#14532d', fontSize: '0.85rem'}}>Net Take Home</span>
              <span style={{...styles.sumVal, color: '#14532d', fontSize: '1rem'}}>₹ {Math.round(netTakeHome).toLocaleString()}</span>
            </div>

            {/* Bank details, as entered on the registration form. Read-only here
                on purpose: this is where the payment gets made from, not where
                account details should be changed — that stays on the one form
                that owns them, so payroll can't quietly diverge from the record. */}
            <div style={styles.bankHeader}>
              <span style={{flex: 1}}>Bank Details</span>
              {canEditBank && (bankEditing ? (
                <>
                  <button type="button" style={styles.bankBtn} onClick={() => setBankEditing(false)} disabled={bankSaving}>Cancel</button>
                  <button type="button" style={{...styles.bankBtnPrimary, cursor: bankSaving ? 'wait' : 'pointer'}} onClick={saveBankDetails} disabled={bankSaving}>
                    {bankSaving ? 'Saving…' : 'Save'}
                  </button>
                </>
              ) : (
                <button type="button" style={styles.bankBtn} onClick={startBankEdit} title="Edit bank details for this nurse">Edit</button>
              ))}
            </div>

            {bankEditing ? (
              BANK_FIELDS.map(f => (
                <div key={f.key} style={styles.bankRow}>
                  <span style={styles.bankLabel}>{f.label}</span>

                  {f.kind === 'reference' ? (
                    // Reads the same reference list the registration form's Bank /
                    // Branch dropdowns use, so entries added in either place show
                    // up in the other. Its menu is portaled, so the narrow summary
                    // panel doesn't clip it.
                    <SearchableSelect
                      options={referenceItems(f.category!, bankDraft[f.key])}
                      value={bankDraft[f.key] ?? ''}
                      onChange={(v) => setBankDraft(prev => ({ ...prev, [f.key]: v }))}
                      onAddNew={async (name) => {
                        await addReferenceOption(f.category!, name);
                        return { id: name, name };
                      }}
                      variant="inline"
                      inputClassName="bankcell"
                      allowClear
                      addNewLabel={f.label.toLowerCase()}
                      placeholder="—"
                      title={`${f.label} — type to search or add`}
                    />
                  ) : f.kind === 'select' ? (
                    <select
                      value={bankDraft[f.key] ?? ''}
                      onChange={(e) => setBankDraft(prev => ({ ...prev, [f.key]: e.target.value }))}
                      style={{...styles.bankInput, cursor: 'pointer'}}
                    >
                      <option value="">—</option>
                      {f.options!.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={bankDraft[f.key] ?? ''}
                      onChange={(e) => setBankDraft(prev => ({ ...prev, [f.key]: e.target.value }))}
                      style={styles.bankInput}
                      placeholder="—"
                    />
                  )}
                </div>
              ))
            ) : hasBankDetails ? (
              bankFields.map(f => (
                <div key={f.key} style={styles.bankRow}>
                  <span style={styles.bankLabel}>{f.label}</span>
                  <span style={{...styles.bankValue, color: f.value ? '#0f172a' : '#94a3b8'}}>
                    {f.value || '—'}
                  </span>
                </div>
              ))
            ) : (
              <div style={{...styles.bankRow, borderBottom: 'none'}}>
                <span style={{fontSize: '0.74rem', color: '#94a3b8', lineHeight: 1.4}}>
                  {canEditBank
                    ? 'No bank details yet — use Edit above to add them.'
                    : 'No bank details yet. Add them on the physio registration form.'}
                </span>
              </div>
            )}

            {/* Sum of just the currently drag-selected range within a summable
                column — separate from Sub Total/Gross Salary, which always
                cover the whole month. Accumulated while the table above
                rendered (see accumulateSelected). */}
            {selectedColumn && SUMMABLE_FIELDS.includes(selectedColumn) && selCount > 0 && (
              <div style={{...styles.sumRow, backgroundColor: '#eef2ff'}}>
                <span style={styles.sumLabel}>Selected: {FILL_COLUMN_LABELS[selectedColumn]} ({selCount})</span>
                <span style={{...styles.sumVal, color: '#3730a3'}}>{Math.round(selSum).toLocaleString('en-IN')}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Selection indicator — the fill happens by typing into a cell, which is
          not self-evident, so say so while a multi-row selection is live. */}
      <div ref={toolbarRef}>
      {selectedRows.size >= 1 && (
        <div
          style={{
            position: 'fixed', bottom: 18, left: '50%', transform: 'translateX(-50%)',
            backgroundColor: '#1e3a8a', color: 'white', padding: '0.5rem 0.95rem',
            borderRadius: '999px', fontSize: '0.78rem', fontWeight: 600, zIndex: 2000,
            boxShadow: '0 6px 18px rgba(0,0,0,0.22)', display: 'flex', alignItems: 'center', gap: '0.7rem',
          }}
        >
          {selectedRows.size} row{selectedRows.size > 1 ? 's' : ''} selected
          {selectedRows.size > 1 && <> — type in <b>Scale</b> or <b>Salary</b> to set them all</>}
          <span style={{ opacity: 0.6 }}>|</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            Highlight:
            {ROW_HIGHLIGHT_COLORS.map((c) => (
              <button
                key={c.value}
                type="button"
                title={c.label}
                onClick={() => applyRowColor(c.value)}
                style={{ width: '18px', height: '18px', borderRadius: '50%', backgroundColor: c.value, border: '1px solid rgba(255,255,255,0.6)', cursor: 'pointer', padding: 0 }}
              />
            ))}
            <button
              type="button"
              title="Clear highlight"
              onClick={() => applyRowColor(null)}
              style={{ width: '18px', height: '18px', borderRadius: '50%', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.6)', cursor: 'pointer', padding: 0, position: 'relative' }}
            >
              <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', lineHeight: 1 }}>✕</span>
            </button>
          </span>
          <span style={{ opacity: 0.6 }}>|</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            Text:
            {TEXT_HIGHLIGHT_COLORS.map((c) => (
              <button
                key={c.value}
                type="button"
                title={c.label}
                onClick={() => applyRemarksColor(c.value)}
                style={{ width: '18px', height: '18px', borderRadius: '4px', backgroundColor: 'white', border: `1px solid ${c.value}`, cursor: 'pointer', padding: 0, color: c.value, fontSize: '11px', fontWeight: 800, lineHeight: '16px' }}
              >A</button>
            ))}
            <button
              type="button"
              title="Clear text color"
              onClick={() => applyRemarksColor(null)}
              style={{ width: '18px', height: '18px', borderRadius: '4px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.6)', cursor: 'pointer', padding: 0, position: 'relative' }}
            >
              <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', lineHeight: 1 }}>✕</span>
            </button>
          </span>
          <span style={{ opacity: 0.6 }}>|</span>
          <button
            type="button"
            onClick={() => { setSelectedRows(new Set()); clearColumnSel(); }}
            style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: 'white', borderRadius: '999px', padding: '3px 10px', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer' }}
          >
            Done
          </button>
        </div>
      )}

      {selectedColumn && selectedCells.size > 0 && (
        <div
          style={{
            position: 'fixed', bottom: 18, left: '50%', transform: 'translateX(-50%)',
            backgroundColor: '#1e3a8a', color: 'white', padding: '0.5rem 0.95rem',
            borderRadius: '999px', fontSize: '0.78rem', fontWeight: 600, zIndex: 2000,
            boxShadow: '0 6px 18px rgba(0,0,0,0.22)', display: 'flex', alignItems: 'center', gap: '0.6rem',
          }}
        >
          {selectedCells.size} <b>{FILL_COLUMN_LABELS[selectedColumn]}</b> cell{selectedCells.size > 1 ? 's' : ''} selected — type a value to fill {selectedCells.size > 1 ? 'them' : 'it'}
          <span style={{ opacity: 0.8 }}>· Esc to clear</span>
        </div>
      )}
      </div>
    </div>
  );
};

export default SalarySheet;